import {createSlice, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {
    ICurrentMachinery,
    IMachinery,
    IMachineryListRow,
    IInputPreset,
    IMachineryInput,
    IMachineryOption,
    IMachineryOutput,
    IThresholdDefaults,
} from "../../../models/iMachinery";
import {
    fetchAddMachinery, fetchUpdateMachineryDispatchParams,
    fetchDeleteMachinery,
    fetchDeleteMachineryPhoto,
    fetchFreezeMachinery,
    fetchGetAllMachinery, fetchGetMachineryByCompany,
    fetchGetMachineryById, fetchGetMachineryOptions, fetchGetThresholdDefaults,
    createInputPreset,
    deleteInputPreset,
    fetchInputPresets,
    fetchMachineryInputs,
    fetchMachineryOutputs,
    fetchUnfreezeMachinery,
    fetchUpdateMachinery, fetchUpdateMachineryTankCapacities,
    runMachineryOutputAction,
    saveMachineryInputs,
    saveMachineryOutputs,
} from "./actions";
import {sortMachineryList} from "../utils/services";

interface IMachineryState {
    list: IMachinery[];
    light_list: IMachineryListRow[];
    options: IMachineryOption[];
    current: ICurrentMachinery | null;
    /**
     * Пороги по умолчанию для текущей машины. null — ещё не загружены или
     * запрос не удался: карточка тогда рисует прочерки, как раньше.
     */
    thresholdDefaults: IThresholdDefaults | null;
    /**
     * Конфигурация выходов терминала текущей машины. null — ещё не загружены
     * или запрос не удался; карточка тогда ничего не рисует, как и до неё.
     */
    outputs: IMachineryOutput[] | null;
    /** Конфигурация входов терминала текущей машины; null — как и у выходов. */
    inputs: IMachineryInput[] | null;
    /**
     * Шаблоны таблиц преобразования. Общие для платформы, поэтому при смене
     * машины НЕ сбрасываются: перезагружать один и тот же список на каждой
     * карточке незачем.
     */
    inputPresets: IInputPreset[] | null;
    isLoading: boolean;
    errorMessage: string;
    wsConnected: boolean;
    wsMessage: string | null;
    /** Фильтр таблицы по типу техники. null = все типы. */
    typeFilter: number | null;
    clientId: string;
}

const initialState: IMachineryState = {
    list: [],
    light_list: [],
    options: [],
    current: null,
    thresholdDefaults: null,
    outputs: null,
    inputs: null,
    inputPresets: null,
    isLoading: false,
    errorMessage: "",
    wsConnected: false,
    wsMessage: null,
    typeFilter: null,
    clientId: "-1",
};

const machineryThunks = [
    fetchGetAllMachinery,
    fetchGetMachineryById,
    fetchUpdateMachinery,
    fetchDeleteMachinery,
    fetchGetMachineryByCompany,
];

const handlePending = (state: IMachineryState) => {
    state.isLoading = true;
    state.errorMessage = "";
};

const handleRejected = (state: any, action: any) => {
    state.isLoading = false;
    state.errorMessage = action.payload as string;
};

export const MachinerySlice = createSlice({
    name: "machinery",
    initialState,
    reducers: {
        wsMachineryConnected: (state) => {
            state.wsConnected = true;
        },
        wsMachineryDisconnected: (state) => {
            state.wsConnected = false;
        },
        wsMachineryMessageReceived: (state, action: PayloadAction<any>) => {
            state.wsMessage = action.payload;
        },
        addMachinery(state, action: PayloadAction<IMachinery | ICurrentMachinery>) {
            state.list = [...state.list, action.payload];
        },
        setCurrentMachinery(state, action: PayloadAction<ICurrentMachinery>) {
            state.current = {...action.payload};
        },
        /**
         * Сброс при уходе со страницы деталей. Без него открытая следом другая
         * машина показывала данные предыдущей, пока не придёт ответ: current
         * оставался заполненным, а карточки считали, что грузить нечего.
         */
        clearCurrentMachinery(state) {
            state.current = null;
            state.thresholdDefaults = null;
            state.outputs = null;
            state.inputs = null;
        },
        updateMachinery(state, action: PayloadAction<IMachinery | ICurrentMachinery>) {
            state.list = state.list.map((machinery) => (machinery.id === action.payload.id ? action.payload : machinery));
        },
        updateMachineryList: (state, action: PayloadAction<IMachinery[]>) => {
            state.list = action.payload;
            state.isLoading = false;
        },
        setMachineryTypeFilter: (state, action: PayloadAction<number | null>) => {
            state.typeFilter = action.payload;
        },
        setLightList: (state, action: PayloadAction<IMachineryListRow[]>) => {
            state.light_list = action.payload;
            state.isLoading = false;
        },
        updateLightListRow: (state, action: PayloadAction<Partial<IMachineryListRow> & { id: string }>) => {
            const idx = state.light_list.findIndex((m) => m.id === action.payload.id);
            if (idx === -1) {
                // Машины в списке ещё нет. Добавляем только ПОЛНУЮ строку:
                // частичный патч (lock-событие до initial) создал бы строку из
                // четырёх полей — с пустыми скоростью, топливом и давлением
                // до следующего пакета трекера. Маркер полноты — brand:
                // он есть в любой строке из toListRow и отсутствует в патче.
                if ('brand' in action.payload) {
                    state.light_list.push(action.payload as IMachineryListRow);
                }
            } else {
                state.light_list[idx] = {...state.light_list[idx], ...action.payload};
            }
        },
        setMachineryClientId: (state, action: PayloadAction<string>) => {
            state.clientId = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGetAllMachinery.fulfilled, (state, action: PayloadAction<IMachinery[]>) => {
                state.list = sortMachineryList(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchGetMachineryById.fulfilled, (state, action: PayloadAction<ICurrentMachinery>) => {
                state.isLoading = false;
                state.current = action.payload;
            })
            .addCase(fetchGetThresholdDefaults.fulfilled, (state, action: PayloadAction<IThresholdDefaults>) => {
                state.thresholdDefaults = action.payload;
            })
            .addCase(fetchMachineryOutputs.fulfilled, (state, action: PayloadAction<IMachineryOutput[]>) => {
                state.outputs = action.payload;
            })
            .addCase(saveMachineryOutputs.fulfilled, (state, action: PayloadAction<IMachineryOutput[]>) => {
                state.outputs = action.payload;
            })
            .addCase(fetchMachineryInputs.fulfilled, (state, action: PayloadAction<IMachineryInput[]>) => {
                state.inputs = action.payload;
            })
            .addCase(saveMachineryInputs.fulfilled, (state, action: PayloadAction<IMachineryInput[]>) => {
                state.inputs = action.payload;
            })
            .addCase(fetchInputPresets.fulfilled, (state, action: PayloadAction<IInputPreset[]>) => {
                state.inputPresets = action.payload;
            })
            .addCase(createInputPreset.fulfilled, (state, action: PayloadAction<IInputPreset>) => {
                // Дописываем в конец и пересортировываем как сервер (role, name) —
                // иначе новый шаблон прыгал бы вниз списка до перезагрузки.
                const next = [...(state.inputPresets ?? []), action.payload];
                state.inputPresets = next.sort(
                    (a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name),
                );
            })
            .addCase(deleteInputPreset.fulfilled, (state, action: PayloadAction<string>) => {
                if (!state.inputPresets) return;
                state.inputPresets = state.inputPresets.filter((p) => p.id !== action.payload);
            })
            .addCase(runMachineryOutputAction.fulfilled, (state, action) => {
                // Команда возвращает только одну строку — подменяем её точечно,
                // остальные три остаются как были.
                if (!state.outputs) return;
                const updated = action.payload.output;
                state.outputs = state.outputs.map((o) =>
                    o.output_index === updated.output_index ? updated : o,
                );
            })
            .addCase(fetchUpdateMachinery.fulfilled, (state, action: PayloadAction<ICurrentMachinery>) => {
                state.isLoading = false;
                state.current = action.payload;
                state.list = [
                    ...state.list.map((machinery) => (machinery.id === action.payload.id ? action.payload : machinery)),
                ];
            })
            .addCase(fetchDeleteMachinery.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                state.list = state.list.filter((m) => m.id !== action.payload);
                state.light_list = state.light_list.filter((m) => m.id !== action.payload);
                state.current = null;
            })
            .addCase(fetchDeleteMachineryPhoto.rejected, handleRejected)
            .addCase(fetchGetMachineryOptions.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchGetMachineryOptions.fulfilled, (state, action: PayloadAction<IMachineryOption[]>) => {
                state.options = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchGetMachineryByCompany.fulfilled, (state, action: PayloadAction<IMachineryListRow[]>) => {
                state.light_list = action.payload;
                state.isLoading = false;
            })

            .addCase(fetchGetMachineryOptions.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchUpdateMachineryTankCapacities.fulfilled, (state, action: PayloadAction<IMachinery>) => {
                if (state.current && state.current.id === action.payload.id) {
                    state.current = {...state.current, ...action.payload};
                }
                state.list = state.list.map((m) => (m.id === action.payload.id ? {...m, ...action.payload} : m));
            })
            .addCase(fetchUpdateMachineryDispatchParams.fulfilled, (state, action: PayloadAction<IMachinery>) => {
                if (state.current && state.current.id === action.payload.id) {
                    state.current = {...state.current, ...action.payload};
                }
                state.list = state.list.map((m) => (m.id === action.payload.id ? {...m, ...action.payload} : m));
            })
            .addCase(fetchAddMachinery.pending, handlePending)
            .addCase(fetchAddMachinery.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchAddMachinery.rejected, handleRejected)
            // Заморозка меняет ровно одно поле — статус основного трекера.
            // Обновляем его точечно из ответа, чтобы не перезапрашивать
            // карточку и список целиком. addMatcher, а не два addCase:
            // builder не принимает addCase после addMatcher, а freeze и
            // unfreeze обрабатываются одинаково.
            // isLoading здесь не трогаем: эти thunks не входят в
            // machineryThunks, спиннер держит сам компонент (как у lock).
            .addMatcher(
                isAnyOf(fetchFreezeMachinery.fulfilled, fetchUnfreezeMachinery.fulfilled),
                (state, action) => {
                    const {id, status} = action.payload;
                    if (state.current && state.current.id === id && state.current.tracker) {
                        state.current = {
                            ...state.current,
                            tracker: {...state.current.tracker, status},
                        };
                    }
                    state.list = state.list.map((m) =>
                        m.id === id && m.tracker ? {...m, tracker: {...m.tracker, status}} : m,
                    );
                },
            )
            .addMatcher(
                (action) => machineryThunks.some(thunk => thunk.pending.match(action)),
                handlePending
            )
            .addMatcher(
                (action) => machineryThunks.some(thunk => thunk.rejected.match(action)),
                handleRejected
            )
    },
});

export const {
    updateMachineryList,
    wsMachineryConnected,
    wsMachineryDisconnected,
    wsMachineryMessageReceived,
    addMachinery,
    setCurrentMachinery,
    clearCurrentMachinery,
    updateMachinery,
    setMachineryTypeFilter,
    // ─── light_list ───
    setLightList,
    updateLightListRow,
    setMachineryClientId,
} = MachinerySlice.actions;
export default MachinerySlice.reducer;